import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layers, Plus, MessageSquareText, Pencil, Trash2, Loader2 } from "lucide-react";

import { Header } from "../layout/Header";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { ShareLinkButton } from "../layout/ShareLinkButton";
import { TopicFormDialog, TopicFormValues } from "./TopicFormDialog";
import { useAppHeaderProps } from "../../lib/useAppHeaderProps";
import { CATEGORIES } from "../../lib/categories";
import { getStoredCurrentUser } from "../../lib/app-events";
import ApiService from "../../services/api-service";
import { showErrorToast, showSuccessToast } from "../../lib/toast";
import { CurrentUser } from "../../types/user";

export interface AiTopic {
  id: number;
  title: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  category_id: number;
  category_name: string | null;
  user_id: number;
  username: string | null;
  prompt_count: number;
}

const categoryOptions = CATEGORIES.slice(1).map((cat: any) => ({ id: cat.id, name: cat.name }));

// /ai-topics - a separate section from the main feed: browse, create, edit
// and delete AI Topics (each one groups related AI Prompts).
export function AiTopicsPage() {
  const { onOpenAuth, onOpenCreateModal } = useAppHeaderProps();
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(getStoredCurrentUser);
  const [topics, setTopics] = useState<AiTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState<AiTopic | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingTopic, setDeletingTopic] = useState<AiTopic | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    document.title = "AI Topics · The Deepam";
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleUserChange = (event: Event) => {
      setCurrentUser(event instanceof CustomEvent ? event.detail ?? null : getStoredCurrentUser());
    };
    window.addEventListener("inspirelens:userchange", handleUserChange);
    return () => window.removeEventListener("inspirelens:userchange", handleUserChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    ApiService.getAiTopics()
      .then((res: { topics?: AiTopic[] }) => {
        if (!cancelled) setTopics(res.topics || []);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [retryToken]);

  const openCreate = () => {
    if (!currentUser) {
      onOpenAuth("login");
      return;
    }
    setEditingTopic(null);
    setFormOpen(true);
  };

  const openEdit = (topic: AiTopic) => {
    setEditingTopic(topic);
    setFormOpen(true);
  };

  const handleFormSubmit = async (values: TopicFormValues) => {
    setIsSaving(true);
    try {
      if (editingTopic) {
        await ApiService.updateAiTopic(editingTopic.id, values);
        setTopics((prev) =>
          prev.map((t) =>
            t.id === editingTopic.id
              ? { ...t, title: values.title, description: values.description, category_id: Number(values.category), category_name: categoryOptions.find((c) => String(c.id) === values.category)?.name ?? t.category_name }
              : t
          )
        );
        showSuccessToast("Topic updated successfully!");
      } else {
        const res = await ApiService.createAiTopic(values);
        if (res?.topic) setTopics((prev) => [res.topic, ...prev]);
        showSuccessToast("Topic created successfully!");
      }
      setFormOpen(false);
      setEditingTopic(null);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Failed to save topic. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingTopic) return;
    setIsDeleting(true);
    try {
      await ApiService.deleteAiTopic(deletingTopic.id);
      setTopics((prev) => prev.filter((t) => t.id !== deletingTopic.id));
      showSuccessToast("Topic deleted successfully!");
      setDeletingTopic(null);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Failed to delete topic. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const totalPrompts = topics.reduce((sum, t) => sum + (t.prompt_count || 0), 0);

  return (
    <div className="max-w-[85rem] mx-auto px-3 py-6">
      <Header currentUser={currentUser} onOpenAuth={onOpenAuth} onOpenCreateModal={onOpenCreateModal} />

      {/* Hero banner - same card/blur-accent/stat-chip recipe as the home
          page's WelcomeHero, so this new section still feels native. */}
      <section className="relative mt-6 overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg">
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          <div className="absolute -top-20 -right-16 h-56 w-56 rounded-full bg-gradient-to-br from-[#fc991d]/30 via-orange-300/20 to-transparent blur-3xl" />
          <div className="absolute -bottom-24 -left-10 h-56 w-56 rounded-full bg-gradient-to-tr from-slate-700/20 via-sky-400/10 to-transparent blur-3xl dark:from-slate-500/20" />
        </div>

        <div className="relative flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-8">
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#fc991d] to-orange-500 text-white shadow-md">
              <Layers className="w-7 h-7" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                <Layers className="w-5 h-5 sm:hidden text-[#fc991d] shrink-0" aria-hidden="true" />
                AI Topics
              </h1>
              <p className="mt-1.5 text-sm sm:text-base text-slate-500 dark:text-slate-400 max-w-md">
                A topic groups related AI Prompts together, each with its own up/down votes.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <ShareLinkButton title="AI Topics" />
            <Button
              onClick={openCreate}
              className="bg-[#fc991d] hover:bg-[#e8890a] text-white shadow-sm"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              New Topic
            </Button>
          </div>
        </div>

        <div className="relative grid grid-cols-2 gap-px border-t border-slate-200 dark:border-slate-700 bg-slate-200/60 dark:bg-slate-700/60">
          <div className="flex items-center gap-3 bg-white dark:bg-slate-900 px-4 py-3 sm:px-6 sm:py-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 shrink-0">
              <Layers className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white leading-none">{topics.length}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">{topics.length === 1 ? "Topic" : "Topics"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white dark:bg-slate-900 px-4 py-3 sm:px-6 sm:py-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 shrink-0">
              <MessageSquareText className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white leading-none">{totalPrompts}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">Total prompts</p>
            </div>
          </div>
        </div>
      </section>

      <div className="mt-6">
        {error ? (
          <div className="text-center py-16 space-y-3">
            <p className="text-lg font-medium text-slate-700 dark:text-slate-200">We couldn't load AI Topics.</p>
            <Button onClick={() => setRetryToken((n) => n + 1)} variant="outline" className="mt-2">
              Try Again
            </Button>
          </div>
        ) : loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="w-full h-40 rounded-2xl" />
            ))}
          </div>
        ) : topics.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 py-16 text-center space-y-3">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-50 text-[#fc991d] dark:bg-orange-500/10 dark:text-orange-300">
              <Layers className="w-6 h-6" />
            </div>
            <p className="text-slate-700 dark:text-slate-200 font-medium">No AI Topics yet.</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">Group a few related prompts together to get started.</p>
            <Button onClick={openCreate} variant="outline" size="sm" className="mt-1">
              <Plus className="w-4 h-4 mr-1.5" />
              Create the first one
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {topics.map((topic) => {
              const isOwner = !!currentUser?.id && Number(currentUser.id) === Number(topic.user_id);
              const initials = (topic.username || "?").slice(0, 2).toUpperCase();
              return (
                <div
                  key={topic.id}
                  className="group relative flex flex-col rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:border-[#fc991d]/40 dark:hover:border-[#fc991d]/40"
                >
                  <div className="flex items-start justify-between gap-2">
                    {topic.category_name ? (
                      <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 px-2.5 py-1 text-xs font-medium">
                        {topic.category_name}
                      </span>
                    ) : (
                      <span />
                    )}
                    {isOwner && (
                      <div className="flex items-center gap-0.5 shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100 transition-opacity">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(topic)} aria-label="Edit topic" className="h-7 w-7 p-0">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeletingTopic(topic)}
                          aria-label="Delete topic"
                          className="h-7 w-7 p-0 text-red-600 hover:text-red-700 dark:text-red-400"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>

                  <Link to={`/ai-topics/${topic.id}`} className="mt-3 flex-1">
                    <h2 className="font-semibold text-slate-900 dark:text-slate-100 line-clamp-2 group-hover:text-[#fc991d] dark:group-hover:text-[#fc991d] transition-colors">
                      {topic.title}
                    </h2>
                    {topic.description && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1.5 line-clamp-2">{topic.description}</p>
                    )}
                  </Link>

                  <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-6 h-6 rounded-full bg-slate-600 dark:bg-slate-500 grid place-items-center text-white font-medium text-[10px] shrink-0">
                        {initials}
                      </div>
                      <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{topic.username || "Unknown"}</span>
                    </div>
                    <span className="flex items-center gap-1 text-xs font-medium text-slate-600 dark:text-slate-300 shrink-0">
                      <MessageSquareText className="w-3.5 h-3.5" />
                      {topic.prompt_count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <TopicFormDialog
        open={formOpen}
        mode={editingTopic ? "edit" : "create"}
        categories={categoryOptions}
        initial={
          editingTopic
            ? { title: editingTopic.title, description: editingTopic.description || "", category: String(editingTopic.category_id) }
            : undefined
        }
        isLoading={isSaving}
        onClose={() => {
          if (!isSaving) {
            setFormOpen(false);
            setEditingTopic(null);
          }
        }}
        onSubmit={handleFormSubmit}
      />

      <Dialog open={!!deletingTopic} onOpenChange={(value: boolean) => !value && !isDeleting && setDeletingTopic(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Topic</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingTopic?.title}"? This also removes every prompt inside it. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="ghost" onClick={() => setDeletingTopic(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting} className="bg-red-600 hover:bg-red-700 text-white">
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Topic
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AiTopicsPage;
